// src/pages/Products/ProductList.js
import { useEffect, useState } from "react";
import { collection, getDocs, deleteDoc, doc, updateDoc, writeBatch } from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import { db, storage } from "../../../db/firebase";
import { Link } from "react-router-dom";
import { Search, Filter, X, Download, ArrowUpDown } from "lucide-react";
import * as XLSX from 'xlsx';

export default function ProductList() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [organizedCategories, setOrganizedCategories] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  
  // Search and Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [stockFilter, setStockFilter] = useState("all");
  const [offerFilter, setOfferFilter] = useState("all");
  const [sortBy, setSortBy] = useState("order"); // Default sort by order field
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(10);

  // Helper function to calculate total stock across all variants
  const calculateTotalStock = (product) => {
    if (!product.PType || !Array.isArray(product.PType)) return 0;
    
    return product.PType.reduce((total, variant) => {
      if (!variant.PStock || !Array.isArray(variant.PStock)) {
        return total + (variant.PStock || 0);
      }
      
      const variantStock = variant.PStock.reduce((sum, stock) => {
        if (stock && typeof stock === 'object' && 'Qty' in stock) {
          return sum + (stock.Qty || 0);
        }
        return sum + (Number(stock) || 0);
      }, 0);
      
      return total + variantStock;
    }, 0);
  };

  // Use the top-level hasActiveOffer field
  const hasAnyOffer = (product) => {
    return product.hasActiveOffer || false;
  };

  // Function to fetch products
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, "products"));
      const productsData = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      
      // Sort products by order field if it exists, otherwise by date added
      const sortedProducts = productsData.sort((a, b) => {
        const orderA = a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
        const orderB = b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
        
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        
        // Fallback to date added if order is the same or doesn't exist
        const dateA = a.PDateAdded ? new Date(a.PDateAdded) : new Date(0);
        const dateB = b.PDateAdded ? new Date(b.PDateAdded) : new Date(0);
        return dateB - dateA;
      });
      
      setProducts(sortedProducts);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories and organize them
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const snap = await getDocs(collection(db, "categories"));
        const categoriesData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setCategories(categoriesData);
        
        // Organize categories into hierarchy
        const organized = organizeCategoriesHierarchy(categoriesData);
        setOrganizedCategories(organized);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, []);

  // NEW: Function to migrate products by adding order field
  const migrateProductsOrder = async () => {
    if (!window.confirm("This will add an order field to all existing products based on their creation date. This action cannot be undone. Continue?")) {
      return;
    }

    try {
      setMigrating(true);
      
      const querySnapshot = await getDocs(collection(db, "products"));
      const productsToMigrate = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      
      // Sort products by date added (newest first) to assign order numbers
      const sortedByDate = productsToMigrate.sort((a, b) => {
        const dateA = a.PDateAdded ? new Date(a.PDateAdded) : new Date(0);
        const dateB = b.PDateAdded ? new Date(b.PDateAdded) : new Date(0);
        return dateB - dateA; // Newest first
      });
      
      // Use batch for better performance
      const batch = writeBatch(db);
      let orderNumber = 1;
      
      sortedByDate.forEach(product => {
        // Only update if order field doesn't exist
        if (product.order === undefined) {
          const productRef = doc(db, "products", product.id);
          batch.update(productRef, { order: orderNumber });
          orderNumber++;
        }
      });
      
      await batch.commit();
      
      alert(`✅ Migration completed! Order field added to ${orderNumber - 1} products.`);
      
      // Refresh products list
      await fetchProducts();
      
    } catch (error) {
      console.error("Error migrating products:", error);
      alert("❌ Error migrating products. Please check console for details.");
    } finally {
      setMigrating(false);
    }
  };

  // NEW: Function to check if migration is needed
  const needsMigration = () => {
    return products.some(product => product.order === undefined);
  };

  // NEW: Function to get migration status
  const getMigrationStatus = () => {
    const totalProducts = products.length;
    const migratedProducts = products.filter(p => p.order !== undefined).length;
    return {
      migrated: migratedProducts,
      total: totalProducts,
      needsMigration: migratedProducts < totalProducts
    };
  };

  // Export to Excel function (existing code)
  const exportToExcel = async () => {
    try {
      setExportLoading(true);
      
      // Prepare data for Excel
      const excelData = products.map(product => {
        const totalStock = calculateTotalStock(product);
        const hasOffer = hasAnyOffer(product);
        const offerVariantCount = getOfferVariantCount(product);
        const variantCount = getVariantCount(product);
        const categoryCount = product.PCategories?.length || 0;
        const priceRange = getPriceRange(product);
        
        // Get category names
        const categoryNames = getCategoryNamesWithHierarchy(product)
          .map(cat => cat.name)
          .join(', ');
        
        // Get variant details
        const variantDetails = product.PType?.map(variant => 
          `${variant.PTypeName || 'Unnamed'}: ${variant.PPrice || 0} PKR${variant.PInOffer ? ` (Offer: ${variant.PDiscountPrice || 0} PKR)` : ''}`
        ).join('; ') || 'No variants';
        
        // Get stock details
        const stockDetails = product.PType?.map(variant => 
          variant.PStock?.map(stock => 
            `${stock.Size || 'No Size'}: ${stock.Qty || 0}`
          ).join(', ') || 'No stock'
        ).join('; ') || 'No stock';

        return {
          'Product ID': product.id,
          'Product Name': product.PName || 'Unnamed Product',
          'Product Code': product.PCode || 'No SKU',
          'Order': product.order || 'Not set',
          'Short Description': product.PDesc || '',
          'Detailed Description': product.PDetailedDesc || '',
          'Price Range': priceRange.min === "N/A" ? 'N/A' : 
            `${priceRange.min} - ${priceRange.max} PKR${priceRange.hasOffer ? ` (Discounted: ${priceRange.minDiscount} - ${priceRange.max} PKR)` : ''}`,
          'Categories': categoryNames,
          'Category Count': categoryCount,
          'Variants': variantCount,
          'Variant Details': variantDetails,
          'Total Stock': totalStock,
          'Stock Details': stockDetails,
          'Offer Status': hasOffer ? 'Active Offers' : 'Standard',
          'Offer Variants': offerVariantCount,
          'Date Added': product.PDateAdded ? new Date(product.PDateAdded).toLocaleDateString() : 'N/A',
          'Has Active Offer': hasOffer ? 'Yes' : 'No',
          'Sole Colors': product.PType?.map(v => v.PSoleColor).filter(Boolean).join(', ') || 'N/A'
        };
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Set column widths
      const colWidths = [
        { wch: 20 }, // Product ID
        { wch: 30 }, // Product Name
        { wch: 15 }, // Product Code
        { wch: 10 }, // Order
        { wch: 40 }, // Short Description
        { wch: 50 }, // Detailed Description
        { wch: 30 }, // Price Range
        { wch: 30 }, // Categories
        { wch: 15 }, // Category Count
        { wch: 10 }, // Variants
        { wch: 50 }, // Variant Details
        { wch: 12 }, // Total Stock
        { wch: 50 }, // Stock Details
        { wch: 15 }, // Offer Status
        { wch: 15 }, // Offer Variants
        { wch: 12 }, // Date Added
        { wch: 15 }, // Has Active Offer
        { wch: 20 }  // Sole Colors
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Products');
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `products_export_${timestamp}.xlsx`;
      
      // Export to Excel
      XLSX.writeFile(wb, filename);
      
      alert(`✅ Products exported successfully! File: ${filename}`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('❌ Error exporting products to Excel. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  // Generic export function (existing code)
  const exportProductsToExcel = async (productsToExport, type = 'all') => {
    try {
      setExportLoading(true);
      
      const excelData = productsToExport.map(product => {
        const totalStock = calculateTotalStock(product);
        const hasOffer = hasAnyOffer(product);
        const offerVariantCount = getOfferVariantCount(product);
        const variantCount = getVariantCount(product);
        const categoryCount = product.PCategories?.length || 0;
        const priceRange = getPriceRange(product);
        
        // Get category names
        const categoryNames = getCategoryNamesWithHierarchy(product)
          .map(cat => cat.name)
          .join(', ');
        
        // Get variant details
        const variantDetails = product.PType?.map(variant => 
          `${variant.PTypeName || 'Unnamed'}: ${variant.PPrice || 0} PKR${variant.PInOffer ? ` (Offer: ${variant.PDiscountPrice || 0} PKR)` : ''}`
        ).join('; ') || 'No variants';
        
        // Get stock details
        const stockDetails = product.PType?.map(variant => 
          variant.PStock?.map(stock => 
            `${stock.Size || 'No Size'}: ${stock.Qty || 0}`
          ).join(', ') || 'No stock'
        ).join('; ') || 'No stock';

        return {
          'Product ID': product.id,
          'Product Name': product.PName || 'Unnamed Product',
          'Product Code': product.PCode || 'No SKU',
          'Order': product.order || 'Not set',
          'Short Description': product.PDesc || '',
          'Detailed Description': product.PDetailedDesc || '',
          'Price Range': priceRange.min === "N/A" ? 'N/A' : 
            `${priceRange.min} - ${priceRange.max} PKR${priceRange.hasOffer ? ` (Discounted: ${priceRange.minDiscount} - ${priceRange.max} PKR)` : ''}`,
          'Categories': categoryNames,
          'Category Count': categoryCount,
          'Variants': variantCount,
          'Variant Details': variantDetails,
          'Total Stock': totalStock,
          'Stock Details': stockDetails,
          'Offer Status': hasOffer ? 'Active Offers' : 'Standard',
          'Offer Variants': offerVariantCount,
          'Date Added': product.PDateAdded ? new Date(product.PDateAdded).toLocaleDateString() : 'N/A',
          'Has Active Offer': hasOffer ? 'Yes' : 'No',
          'Sole Colors': product.PType?.map(v => v.PSoleColor).filter(Boolean).join(', ') || 'N/A'
        };
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Set column widths
      const colWidths = [
        { wch: 20 }, { wch: 30 }, { wch: 15 }, { wch: 10 }, { wch: 40 }, 
        { wch: 50 }, { wch: 30 }, { wch: 30 }, { wch: 15 }, { wch: 10 }, 
        { wch: 50 }, { wch: 12 }, { wch: 50 }, { wch: 15 }, { wch: 15 }, 
        { wch: 12 }, { wch: 15 }, { wch: 20 }
      ];
      ws['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, 'Products');
      
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `products_${type}_export_${timestamp}.xlsx`;
      
      XLSX.writeFile(wb, filename);
      
      alert(`✅ ${productsToExport.length} product(s) exported successfully! File: ${filename}`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('❌ Error exporting products to Excel. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  // Filter products based on search and filters
  const filteredProducts = products.filter(product => {
    // Search filter
    const matchesSearch = 
      searchTerm === "" ||
      (product.PName && product.PName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.PCode && product.PCode.toLowerCase().includes(searchTerm.toLowerCase()));

    // Category filter
    const matchesCategory = 
      selectedCategory === "all" ||
      (product.PCategories && product.PCategories.includes(selectedCategory));

    // Stock filter
    const totalStock = calculateTotalStock(product);
    const matchesStock = 
      stockFilter === "all" ||
      (stockFilter === "in_stock" && totalStock > 0) ||
      (stockFilter === "out_of_stock" && totalStock === 0);

    // Offer filter
    const hasOffer = hasAnyOffer(product);
    const matchesOffer = 
      offerFilter === "all" ||
      (offerFilter === "with_offers" && hasOffer) ||
      (offerFilter === "without_offers" && !hasOffer);

    return matchesSearch && matchesCategory && matchesStock && matchesOffer;
  });

  // Sort filtered products
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case "order":
        const orderA = a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
        const orderB = b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      
      case "name":
        return (a.PName || '').localeCompare(b.PName || '');
      
      case "date":
        const dateA = a.PDateAdded ? new Date(a.PDateAdded) : new Date(0);
        const dateB = b.PDateAdded ? new Date(b.PDateAdded) : new Date(0);
        return dateB - dateA;
      
      case "stock":
        const stockA = calculateTotalStock(a);
        const stockB = calculateTotalStock(b);
        return stockB - stockA;
      
      default:
        return 0;
    }
  });

  // Pagination logic
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = sortedProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(sortedProducts.length / productsPerPage);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, stockFilter, offerFilter, sortBy]);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Go to next page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Go to previous page
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      // Show all pages if total pages are less than or equal to maxPagesToShow
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Show limited pages with ellipsis
      if (currentPage <= 3) {
        // Near the start
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near the end
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        // In the middle
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  // Function to organize categories into hierarchy
  const organizeCategoriesHierarchy = (categoriesData) => {
    const mainCategories = categoriesData.filter(cat => !cat.ParentCategory);
    const subCategories = categoriesData.filter(cat => cat.ParentCategory);

    // Add subcategories to their parent categories
    const categoriesWithChildren = mainCategories.map(mainCat => ({
      ...mainCat,
      subcategories: subCategories.filter(subCat => subCat.ParentCategory === mainCat.id)
    }));

    return categoriesWithChildren;
  };

  // Function to get category names with hierarchy for a product
  const getCategoryNamesWithHierarchy = (product) => {
    if (!product?.PCategories || !organizedCategories.length) return [];

    const selectedCategories = [];
    
    organizedCategories.forEach(mainCat => {
      // Check if main category is selected
      if (product.PCategories.includes(mainCat.id)) {
        selectedCategories.push({
          id: mainCat.id,
          name: mainCat.CategoryName,
          type: 'main',
          hasSubcategories: mainCat.subcategories && mainCat.subcategories.length > 0
        });
      }
      
      // Check subcategories
      if (mainCat.subcategories) {
        mainCat.subcategories.forEach(subCat => {
          if (product.PCategories.includes(subCat.id)) {
            selectedCategories.push({
              id: subCat.id,
              name: subCat.CategoryName,
              type: 'sub',
              parentName: mainCat.CategoryName
            });
          }
        });
      }
    });

    return selectedCategories;
  };

  // Function to display categories in compact hierarchical view
  const renderCompactCategories = (product) => {
    const categoryHierarchy = getCategoryNamesWithHierarchy(product);
    
    if (categoryHierarchy.length === 0) {
      return <span className="text-gray-400 text-sm">No categories</span>;
    }

    // Group by main categories
    const mainCategories = categoryHierarchy.filter(cat => cat.type === 'main');
    const subcategories = categoryHierarchy.filter(cat => cat.type === 'sub');
    
    // Create a map of main categories and their subcategories
    const categoryMap = {};
    
    mainCategories.forEach(mainCat => {
      categoryMap[mainCat.name] = {
        main: mainCat,
        subs: subcategories.filter(sub => sub.parentName === mainCat.name)
      };
    });
    
    // Handle subcategories without main category
    const orphanSubs = subcategories.filter(sub => 
      !mainCategories.some(main => main.name === sub.parentName)
    );

    return (
      <div className="flex flex-col gap-1">
        {/* Main categories with their subcategories */}
        {Object.values(categoryMap).map(({ main, subs }) => (
          <div key={main.id} className="flex flex-col">
            <div className="flex items-center gap-1">
              <span className="text-xs font-medium text-purple-700">{main.name}</span>
              {subs.length > 0 && (
                <span className="text-xs text-gray-500">({subs.length})</span>
              )}
            </div>
            {subs.length > 0 && (
              <div className="ml-2 flex flex-wrap gap-1">
                {subs.map(sub => (
                  <span key={sub.id} className="text-xs text-blue-600 bg-blue-50 px-1 rounded">
                    {sub.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
        
        {/* Orphan subcategories */}
        {orphanSubs.length > 0 && (
          <div className="flex flex-col">
            <span className="text-xs text-gray-500 italic">Individual subcategories:</span>
            <div className="flex flex-wrap gap-1 ml-2">
              {orphanSubs.map(sub => (
                <span key={sub.id} className="text-xs text-blue-600 bg-blue-50 px-1 rounded">
                  {sub.name}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Simple count for many categories */}
        {categoryHierarchy.length > 6 && (
          <span className="text-xs text-gray-500 mt-1">
            +{categoryHierarchy.length - 6} more categories
          </span>
        )}
      </div>
    );
  };

  // Function to delete files from Firebase Storage
  const deleteStorageFiles = async (fileUrls) => {
    if (!fileUrls || !Array.isArray(fileUrls)) return;

    const deletePromises = fileUrls.map(async (fileUrl) => {
      try {
        const matches = fileUrl.match(/o\/(.+?)\?/);
        if (matches && matches[1]) {
          const filePath = decodeURIComponent(matches[1]);
          const fileRef = ref(storage, filePath);
          await deleteObject(fileRef);
          console.log('Deleted file:', filePath);
        }
      } catch (error) {
        console.error('Error deleting file:', fileUrl, error);
      }
    });

    await Promise.all(deletePromises);
  };

  // Function to delete all media files for a product
  const deleteProductMedia = async (product) => {
    if (!product.PType || !Array.isArray(product.PType)) return;

    const allMediaDeletions = product.PType.map(async (variant) => {
      const imageDeletions = deleteStorageFiles(variant.PImgUrls);
      const videoDeletions = deleteStorageFiles(variant.PVidUrls);
      await Promise.all([imageDeletions, videoDeletions]);
    });

    await Promise.all(allMediaDeletions);
  };

  // Function to handle deletion
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product? This will also delete all associated images and videos.")) {
      return;
    }

    try {
      setDeletingId(id);
      
      const productToDelete = products.find(p => p.id === id);
      if (!productToDelete) {
        throw new Error("Product not found");
      }

      await deleteProductMedia(productToDelete);
      await deleteDoc(doc(db, "products", id));

      setProducts(products.filter((p) => p.id !== id));
      alert("Product and all associated media deleted successfully!");
    } catch (error) {
      console.error("Error deleting product:", error);
      alert("Error deleting product. Check console for details.");
    } finally {
      setDeletingId(null);
    }
  };

  // Helper function to get price range from variants
  const getPriceRange = (product) => {
    if (!product.PType || !Array.isArray(product.PType) || product.PType.length === 0) {
      return { min: "N/A", max: "N/A", hasOffer: false };
    }

    const prices = product.PType
      .map(variant => variant.PPrice || 0)
      .filter(price => price > 0);
    
    if (prices.length === 0) {
      return { min: "N/A", max: "N/A", hasOffer: false };
    }

    const discountPrices = product.PType
      .filter(variant => variant.PInOffer && variant.PDiscountPrice > 0)
      .map(variant => variant.PDiscountPrice);

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const minDiscountPrice = discountPrices.length > 0 ? Math.min(...discountPrices) : null;

    return {
      min: minPrice,
      max: maxPrice,
      minDiscount: minDiscountPrice,
      hasOffer: discountPrices.length > 0
    };
  };

  // Helper component for displaying price range
  const PriceRangeDisplay = ({ product }) => {
    const priceRange = getPriceRange(product);
    
    if (priceRange.min === "N/A") {
      return <span className="text-gray-500">No prices</span>;
    }

    if (priceRange.min === priceRange.max) {
      return (
        <div className="flex flex-col">
          {priceRange.hasOffer ? (
            <>
              <span className="text-sm line-through text-gray-500">PKR{priceRange.min.toFixed(2)}</span>
              <span className="text-red-600 font-bold">PKR{priceRange.minDiscount.toFixed(2)}</span>
            </>
          ) : (
            <span>PKR{priceRange.min.toFixed(2)}</span>
          )}
        </div>
      );
    }

    return (
      <div className="flex flex-col">
        {priceRange.hasOffer ? (
          <>
            <span className="text-sm line-through text-gray-500">
              PKR {priceRange.min.toFixed(2)} - PKR {priceRange.max.toFixed(2)}
            </span>
            <span className="text-red-600 font-bold">PKR{priceRange.minDiscount.toFixed(2)} - PKR{priceRange.max.toFixed(2)}</span>
          </>
        ) : (
          <span>PKR{priceRange.min.toFixed(2)} - PKR{priceRange.max.toFixed(2)}</span>
        )}
      </div>
    );
  };

  // Helper function to count variants with offers
  const getOfferVariantCount = (product) => {
    return product.PType?.filter(variant => variant.PInOffer).length || 0;
  };

  // Helper function to get variant count
  const getVariantCount = (product) => {
    return product.PType?.length || 0;
  };

  // Helper function to safely get variant names
  const getVariantNames = (product) => {
    if (!product.PType || !Array.isArray(product.PType)) return [];
    
    return product.PType
      .map(variant => variant.PTypeName || "Unnamed Variant")
      .filter(name => name && name !== "Unnamed Variant");
  };

  // Helper function to safely get first image
  const getFirstImage = (product) => {
    if (!product.PType || !Array.isArray(product.PType) || product.PType.length === 0) {
      return null;
    }
    
    const firstVariant = product.PType[0];
    return firstVariant.PImgUrls?.[0] || null;
  };

  // Helper function to count total categories across all products
  const getTotalCategoriesCount = () => {
    return products.reduce((total, product) => total + (product.PCategories?.length || 0), 0);
  };

  // Helper function to count products with multiple categories
  const getProductsWithMultipleCategories = () => {
    return products.filter(product => (product.PCategories?.length || 0) > 1).length;
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategory("all");
    setStockFilter("all");
    setOfferFilter("all");
    setSortBy("order");
  };

  // Check if any filter is active
  const isFilterActive = searchTerm !== "" || selectedCategory !== "all" || stockFilter !== "all" || offerFilter !== "all" || sortBy !== "order";

  if (loading) {
    return <div className="p-6">Loading products...</div>;
  }

  const migrationStatus = getMigrationStatus();

  return (
    <div className="p-6">
      <div className="mb-8 flex justify-between items-center">
        " "
      </div>
      
      {/* Migration Banner */}
      {migrationStatus.needsMigration && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-yellow-800 font-medium">Order Migration Required</h3>
              <p className="text-yellow-700 text-sm mt-1">
                {migrationStatus.migrated} of {migrationStatus.total} products have order numbers. 
                Run migration to add order numbers to all products.
              </p>
            </div>
            <button
              onClick={migrateProductsOrder}
              disabled={migrating}
              className="bg-yellow-600 px-4 py-2 text-white rounded-lg hover:bg-yellow-700 transition font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowUpDown size={18} />
              {migrating ? 'Migrating...' : 'Migrate Orders'}
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-purple-800">
          Products ({sortedProducts.length})
          {sortedProducts.length !== products.length && (
            <span className="text-sm font-normal text-gray-600 ml-2">
              (Filtered from {products.length} total)
            </span>
          )}
          {sortedProducts.length > productsPerPage && (
            <span className="text-sm font-normal text-gray-600 ml-2">
              (Showing {indexOfFirstProduct + 1}-{Math.min(indexOfLastProduct, sortedProducts.length)} of {sortedProducts.length})
            </span>
          )}
        </h2>
        <div className="flex gap-3">
          {/* Export Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => exportProductsToExcel(products, 'all')}
              disabled={exportLoading || products.length === 0}
              className="bg-green-600 px-4 py-2 text-white rounded-lg hover:bg-green-700 transition font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={18} />
              {exportLoading ? 'Exporting...' : 'Export All'}
            </button>
            {isFilterActive && (
              <button
                onClick={() => exportProductsToExcel(sortedProducts, 'filtered')}
                disabled={exportLoading || sortedProducts.length === 0}
                className="bg-blue-600 px-4 py-2 text-white rounded-lg hover:bg-blue-700 transition font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={18} />
                {exportLoading ? 'Exporting...' : 'Export Filtered'}
              </button>
            )}
          </div>
<Link
  to="/admin/products/order"
  className="bg-purple-600 px-4 py-2 text-white rounded-lg hover:bg-purple-700 transition font-medium flex items-center gap-2"
>
  <ArrowUpDown size={18} />
  Manage Order
</Link>
          <Link
            to="/admin/products/new"
            className="bg-purple-700 px-6 py-3 text-white rounded-lg hover:bg-purple-800 transition font-medium"
          >
            + Add New Product
          </Link>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          {/* Search Input */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Products
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name, SKU, description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              {organizedCategories.map(category => (
                <optgroup key={category.id} label={category.CategoryName}>
                  <option value={category.id}>{category.CategoryName} (All)</option>
                  {category.subcategories?.map(subcategory => (
                    <option key={subcategory.id} value={subcategory.id}>
                      └ {subcategory.CategoryName}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Stock Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Stock Status
            </label>
            <select
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Stock</option>
              <option value="in_stock">In Stock</option>
              <option value="out_of_stock">Out of Stock</option>
            </select>
          </div>

          {/* Offer Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Offer Status
            </label>
            <select
              value={offerFilter}
              onChange={(e) => setOfferFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Products</option>
              <option value="with_offers">With Offers</option>
              <option value="without_offers">Without Offers</option>
            </select>
          </div>

          {/* Sort Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sort By
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="order">Order Number</option>
              <option value="name">Product Name</option>
              <option value="date">Date Added</option>
              <option value="stock">Stock Level</option>
            </select>
          </div>
        </div>

        {/* Active Filters Bar */}
        {isFilterActive && (
          <div className="mt-4 flex items-center justify-between p-3 bg-purple-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <Filter size={16} className="text-purple-600" />
              <span className="text-sm font-medium text-purple-800">Active Filters:</span>
              {searchTerm && (
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs">
                  Search: "{searchTerm}"
                </span>
              )}
              {selectedCategory !== "all" && (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                  Category: {categories.find(c => c.id === selectedCategory)?.CategoryName || selectedCategory}
                </span>
              )}
              {stockFilter !== "all" && (
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                  Stock: {stockFilter === "in_stock" ? "In Stock" : "Out of Stock"}
                </span>
              )}
              {offerFilter !== "all" && (
                <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs">
                  Offers: {offerFilter === "with_offers" ? "With Offers" : "Without Offers"}
                </span>
              )}
              {sortBy !== "order" && (
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs">
                  Sort: {sortBy === 'name' ? 'Name' : sortBy === 'date' ? 'Date' : 'Stock'}
                </span>
              )}
            </div>
            <button
              onClick={clearFilters}
              className="text-sm text-purple-600 hover:text-purple-800 font-medium flex items-center space-x-1"
            >
              <X size={16} />
              <span>Clear All</span>
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="p-4 border-b font-semibold text-gray-700">Order</th>
              <th className="p-4 border-b font-semibold text-gray-700">Product Name</th>
              <th className="p-4 border-b font-semibold text-gray-700">Price Range</th>
              <th className="p-4 border-b font-semibold text-gray-700">Categories</th>
              <th className="p-4 border-b font-semibold text-gray-700">Variants</th>
              <th className="p-4 border-b font-semibold text-gray-700">Total Stock</th>
              <th className="p-4 border-b font-semibold text-gray-700">Offer Status</th>
              <th className="p-4 border-b font-semibold text-gray-700">Date Added</th>
              <th className="p-4 border-b font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentProducts.length > 0 ? (
              currentProducts.map((product) => {
                const totalStock = calculateTotalStock(product);
                const hasOffer = hasAnyOffer(product);
                const offerVariantCount = getOfferVariantCount(product);
                const variantCount = getVariantCount(product);
                const variantNames = getVariantNames(product);
                const firstImage = getFirstImage(product);
                const categoryCount = product.PCategories?.length || 0;

                return (
                  <tr key={product.id} className="border-b hover:bg-gray-50 transition-colors">
                    <td className="p-4 border-b">
                      <div className="flex items-center justify-center">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          product.order !== undefined 
                            ? 'bg-purple-100 text-purple-800 border border-purple-200'
                            : 'bg-gray-100 text-gray-500 border border-gray-200'
                        }`}>
                          {product.order !== undefined ? product.order : 'Not set'}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 border-b">
                      <div className="flex items-center space-x-3">
                        {firstImage && (
                          <img 
                            src={firstImage} 
                            alt={product.PName}
                            className="w-12 h-12 object-cover rounded border"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <div>
                          <div className="font-medium text-gray-900">{product.PName || "Unnamed Product"}</div>
                          <div className="text-sm text-gray-500">{product.PCode || "No SKU"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 border-b">
                      <PriceRangeDisplay product={product} />
                    </td>
                    <td className="p-4 border-b">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            categoryCount > 0 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {categoryCount} categor{categoryCount !== 1 ? 'ies' : 'y'}
                          </span>
                        </div>
                        {renderCompactCategories(product)}
                      </div>
                    </td>
                    <td className="p-4 border-b">
                      <div className="flex flex-col">
                        <span className="font-medium">{variantCount} variant{variantCount !== 1 ? 's' : ''}</span>
                        {variantNames.length > 0 && (
                          <div className="text-xs text-gray-500 mt-1">
                            {variantNames.slice(0, 2).join(', ')}
                            {variantNames.length > 2 && ` +${variantNames.length - 2} more`}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-4 border-b">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        totalStock > 0 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {totalStock} units
                      </span>
                    </td>
                    <td className="p-4 border-b">
                      <div className="flex flex-col gap-1">
                        <span
                          className={`px-3 py-1 text-sm rounded-full font-medium ${
                            hasOffer
                              ? "bg-green-100 text-green-800 border border-green-200"
                              : "bg-gray-100 text-gray-800 border border-gray-200"
                          }`}
                        >
                          {hasOffer ? "Active Offers" : "Standard"}
                        </span>
                        {hasOffer && (
                          <span className="text-xs text-gray-600 text-center">
                            {offerVariantCount} variant{offerVariantCount !== 1 ? 's' : ''} on offer
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 border-b text-gray-600">
                      {product.PDateAdded ? new Date(product.PDateAdded).toLocaleDateString() : "N/A"}
                    </td>
                    <td className="p-4 border-b">
                      <div className="flex space-x-2">
                        <Link
                          to={`/admin/products/${product.id}/edit`}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition font-medium"
                        >
                          Edit
                        </Link>
                        <Link
                          to={`/admin/products/${product.id}/view`}
                          className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700 transition font-medium"
                        >
                          View
                        </Link>
                        <button
                          onClick={() => handleDelete(product.id)}
                          disabled={deletingId === product.id}
                          className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition font-medium disabled:bg-red-400 disabled:cursor-not-allowed"
                        >
                          {deletingId === product.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="9" className="p-8 text-center text-gray-500">
                  <div className="flex flex-col items-center justify-center">
                    <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m8-8V4a1 1 0 00-1-1h-2a1 1 0 00-1 1v1M9 7h6" />
                    </svg>
                    <p className="text-lg font-medium mb-2">No products found</p>
                    <p className="text-sm mb-4">
                      {isFilterActive 
                        ? "Try adjusting your filters to see more results" 
                        : "Get started by adding your first product"
                      }
                    </p>
                    {isFilterActive ? (
                      <button
                        onClick={clearFilters}
                        className="bg-purple-700 px-4 py-2 text-white rounded hover:bg-purple-800 transition font-medium"
                      >
                        Clear Filters
                      </button>
                    ) : (
                      <Link
                        to="/admin/products/new"
                        className="bg-purple-700 px-4 py-2 text-white rounded hover:bg-purple-800 transition font-medium"
                      >
                        + Add New Product
                      </Link>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {sortedProducts.length > productsPerPage && (
        <div className="flex justify-center items-center mt-6 space-x-2">
          <button
            onClick={prevPage}
            disabled={currentPage === 1}
            className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          {getPageNumbers().map((number, index) => (
            number === '...' ? (
              <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-500">
                ...
              </span>
            ) : (
              <button
                key={number}
                onClick={() => paginate(number)}
                className={`px-4 py-2 text-sm font-medium rounded-lg ${
                  currentPage === number
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {number}
              </button>
            )
          ))}
          
          <button
            onClick={nextPage}
            disabled={currentPage === totalPages}
            className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* Summary Statistics */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="text-sm font-medium text-gray-500">Total Products</div>
          <div className="text-2xl font-bold text-gray-900">{products.length}</div>
          <div className="text-xs text-gray-500 mt-1">
            {sortedProducts.length} filtered
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="text-sm font-medium text-gray-500">With Order Numbers</div>
          <div className="text-2xl font-bold text-purple-600">
            {migrationStatus.migrated}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            of {migrationStatus.total} total
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="text-sm font-medium text-gray-500">Active Offers</div>
          <div className="text-2xl font-bold text-green-600">
            {products.filter(p => p.hasActiveOffer).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="text-sm font-medium text-gray-500">Out of Stock</div>
          <div className="text-2xl font-bold text-red-600">
            {products.filter(p => calculateTotalStock(p) === 0).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="text-sm font-medium text-gray-500">Total Variants</div>
          <div className="text-2xl font-bold text-blue-600">
            {products.reduce((sum, product) => sum + (product.PType?.length || 0), 0)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="text-sm font-medium text-gray-500">Total Categories</div>
          <div className="text-2xl font-bold text-purple-600">
            {getTotalCategoriesCount()}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {getProductsWithMultipleCategories()} products with multiple categories
          </div>
        </div>
      </div>
    </div>
  );
}